<template>
    <Head title="Liste des catégories d'articles" />

    <AppLayout>
        <div x-data="handleSelect">
            <Datatables>
                <template #title>
                    Liste des catégories d'articles
                    <span class="font-medium">({{ categories.length }})</span>
                </template>

                <template #headerButtons>
                    <button v-if="$page.props.acl.isAdmin || $page.props.acl.isCommercial"
                        class="rounded-md py-1 px-2 bg-white text-sky hover:bg-sky-400 hover:text-white"
                        @click="showModal()" title="Ajouter"
                    >
                        <i class="fad fa-plus-circle"></i>
                    </button>
                </template>

                <template #tableHeader>
                    <th>#</th>
                    <th>Nom</th>
                    <th>Slug</th>
                    <th>Nombre d'articles liés</th>
                    <th>&nbsp;</th>
                </template>

                <template #tableBody>
                    <tr v-for="(category, index) in categories" :key="category.id">
                        <td>{{ index + 1 }}</td>
                        <td>{{ category.name }}</td>
                        <td>{{ category.slug }}</td>
                        <td>{{ category.items_count }}</td>
                        <td>
                            <div class="flex justify-end items-center space-x-5">
                                <i v-if="!category.items_count" @click="showModal(category)"
                                    class="fad fa-pen-alt text-sky hover:text-sky-700 cursor-pointer" title="Modifier"
                                ></i>
                                <i v-else class="fad fa-pen-alt text-gray-500 opacity-50 cursor-not-allowed" title="Impossible de modifier"></i>

                                <i v-if="!category.items_count" @click="destroyCategory(category)"
                                    class="fad fa-trash text-red-500 hover:text-red-600 cursor-pointer" title="Supprimer"
                                ></i>
                                <i v-else class="fad fa-trash text-gray-500 opacity-50 cursor-not-allowed" title="Impossible de supprimer"></i>
                            </div>
                        </td>
                    </tr>
                </template>
            </Datatables>
        </div>

        <JetDialogModal :show="manageModal" @close="closeModal" max-width="xl">
            <template #title> {{ category ? 'Modifier la catégorie' : 'Ajouter une nouvelle catégorie' }}</template>

            <template #content>
                <div class="grid grid-cols-1 gap-4">
                    <div class="col-span-1 sm:col-span-1">
                        <JetLabelRequired for="name" value="Nom de la catégorie" />
                        <JetInput id="name" type="text" v-model="form.name" maxlength="30" minlength="2"
                            placeholder="Entrer le nom de la catégorie" autocomplete="off"
                        />
                        <JetInputError :message="form.errors.name" />
                    </div>
                </div>
            </template>

            <template #footer>
                <BotiButton @click="closeModal" text="Quitter" color="red">
                    <template #icon><i class="fad fa-times-circle text-xs ml-1"></i></template>
                </BotiButton>

                <BotiButton v-if="category" :loader="form.processing" :disabled="form.processing"
                    color="sky"
                    @click="updateCategory"
                    class="ml-2" :class="{ 'opacity-25': form.processing }"
                    text="modifier"
                >
                    <svg v-if="form.processing" class="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                        <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                    </svg>
                    <template #icon>
                        <i class="fad fa-user-edit text-xs ml-1"></i>
                    </template>
                </BotiButton>

                <BotiButton v-else :loader="form.processing" :disabled="form.processing"
                    @click="storeCategory"
                    class="ml-2" :class="{ 'opacity-25': form.processing }"
                    text="Enregistrer"
                >
                    <svg v-if="form.processing" class="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                        <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                    </svg>
                    <template #icon>
                        <i class="fad fa-check-circle text-xs ml-1"></i>
                    </template>
                </BotiButton>
            </template>
        </JetDialogModal>
    </AppLayout>
</template>

<script>
    import JetInput from '@/Jetstream/Input'
    import BotiButton from '@/Components/Button'
    import AppLayout from '@/Layouts/AppLayout.vue'
    import { Head } from '@inertiajs/inertia-vue3';
    import Datatables from '@/Components/Datatables'
    import JetInputError from '@/Jetstream/InputError'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import JetLabelRequired from '@/Jetstream/LabelRequired'

    export default {
        components: {
            Head,
            JetInput,
            AppLayout,
            BotiButton,
            Datatables,
            JetInputError,
            JetDialogModal,
            JetLabelRequired
        },

        props: {
            categories: Object
        },

        data() {
            return {
                manageModal: false,
                category: null,
                form: this.$inertia.form({})
            }
        },

        methods: {
            closeModal() {
                this.manageModal = false
                this.form.clearErrors()
                this.form.reset()
            },

            showModal(category) {
                if (category) {
                    this.category = category
                    this.form = this.$inertia.form(category)
                } else {
                    this.category = null
                    this.form = this.$inertia.form({
                        name: null
                    })
                }
                
                this.manageModal = true
            },

            storeCategory() {
                this.form.post(route('categories.store'), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.closeModal()
                        this.$swal.fire(
                            'Catégorie',
                            'Catégorie ajoutée avec succès.',
                            'success'
                        )
                    },
                })
            },

            updateCategory() {
                this.form.put(route('categories.update', this.category), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.closeModal()
                        this.$swal.fire(
                            'Catégorie',
                            'Catégorie modifiée avec succès.',
                            'success'
                        )
                    },
                })
            },

            destroyCategory(category) {
                this.$swal.queue([{
                    title: "Catégorie",
                    text: "Etes vous sûr de vouloir supprimer la catégorie ?",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: 'rgb(241, 162, 0)',
                    cancelButtonColor: '#bd081c',
                    confirmButtonText: "Supprimer",
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        return this.$inertia.delete(route('categories.destroy', {'category': category}), {
                            preserveScroll: true,
                            preserveState: true,
                            onSuccess: () => {
                                this.$swal.fire(
                                    'Catégorie',
                                    'Catégorie supprimée avec succès.',
                                    'success'
                                )
                            },
                            onError: (error) => {
                                this.$swal.fire({
                                    icon: 'error',
                                    title: error.message
                                })
                            }
                        })
                    }
                }])
            }
        }
    }
</script>